// Money-loop state derived from a truck's customer-facing estimates
// (truck.estimates, written by EstimatePanel). Falls back to the internal
// repair-category cost guess when no estimate exists yet.

const STAGE_RANK = { paid: 0, invoiced: 1, approved: 2, sent: 3, draft: 4, declined: 5 };

export const MONEY_STAGES = ['none', 'draft', 'sent', 'approved', 'declined', 'invoiced', 'paid'];

const DAY_MS = 86_400_000;
const DEFAULT_TERMS_DAYS = 30;

function toMs(ts) {
  if (!ts) return null;
  return ts < 10_000_000_000 ? ts * 1000 : ts;
}

export function activeEstimate(truck) {
  const list = truck?.estimates || [];
  if (!list.length) return null;
  return [...list].sort((a, b) => {
    const rank = (STAGE_RANK[a.status] ?? 5) - (STAGE_RANK[b.status] ?? 5);
    if (rank) return rank;
    return (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0);
  })[0];
}

// Sum of recorded payments (deposits + partials) against an estimate.
export function paidToDate(est) {
  return (est?.payments || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
}

// What the customer still owes on an invoice.
export function remainingBalance(est) {
  return Math.max(0, (parseFloat(est?.total) || 0) - paidToDate(est));
}

// When an invoice is due, based on invoicedAt + payment terms.
export function invoiceDueMs(est) {
  const invoiced = toMs(est?.invoicedAt);
  if (!invoiced) return null;
  return invoiced + (est.paymentTermsDays || DEFAULT_TERMS_DAYS) * DAY_MS;
}

// > 0 when the invoice is past its terms.
export function invoiceOverdueMs(est, now = Date.now()) {
  const due = invoiceDueMs(est);
  if (!due) return 0;
  return Math.max(0, now - due);
}

export function moneyState(truck, now = Date.now()) {
  const est = activeEstimate(truck);
  if (!est) {
    const internal = (truck?.repairCategories || [])
      .reduce((sum, cat) => sum + (parseFloat(cat.estimatedCost) || 0), 0);
    return { stage: 'none', total: internal, internal: true, stallMs: null, approvedBy: null, overdueMs: 0 };
  }

  const stage = STAGE_RANK[est.status] !== undefined ? est.status : 'draft';
  let stallAnchor = null;
  if (stage === 'sent') stallAnchor = toMs(est.sentAt);
  if (stage === 'invoiced') stallAnchor = toMs(est.invoicedAt);

  const total = parseFloat(est.total) || 0;
  const paid = paidToDate(est);

  return {
    stage,
    total,
    paid,
    // What's still collectible: the open balance on invoices, the full
    // total everywhere else.
    remaining: stage === 'invoiced' ? Math.max(0, total - paid) : total,
    internal: false,
    stallMs: stallAnchor ? Math.max(0, now - stallAnchor) : null,
    approvedBy: est.approvedBy || null,
    overdueMs: stage === 'invoiced' ? invoiceOverdueMs(est, now) : 0,
  };
}

// Public customer-facing approval link (same URL scheme EstimatePanel uses).
export function estimateApprovalUrl(token) {
  const configured = process.env.REACT_APP_PUBLIC_APP_URL || window.localStorage?.getItem('rekon_public_app_url');
  const origin = String(configured || window.location.origin).replace(/\/+$/, '');
  return `${origin}/estimate/${encodeURIComponent(token)}`;
}

// The three money queues (the Money workspace + its nav badge): jobs waiting
// on a customer decision, finished work not yet invoiced, and invoices with
// an open balance. A job can be in at most one queue.
export function moneyQueues(trucks, now = Date.now()) {
  const unapproved = [];
  const unbilled = [];
  const unpaid = [];
  for (const truck of trucks || []) {
    if (truck.status === 'voided') continue;
    const m = moneyState(truck, now);
    if (m.stage === 'invoiced' && m.remaining > 0) {
      unpaid.push({ truck, money: m });
    } else if (m.stage === 'sent' || m.stage === 'declined') {
      unapproved.push({ truck, money: m });
    } else if (['done', 'completed'].includes(truck.status) && !['invoiced', 'paid'].includes(m.stage)) {
      // Work finished but never invoiced — includes jobs with no estimate at
      // all (stage 'none'): completed work with zero paper trail.
      unbilled.push({ truck, money: m });
    }
  }
  return { unapproved, unbilled, unpaid };
}

// Shop-level dollar totals per money-loop stage. Invoiced/overdue count the
// OPEN BALANCE (total minus payments); `overdue` is the slice of invoiced
// past its payment terms.
export function moneyTotals(trucks, now = Date.now()) {
  const totals = { draft: 0, sent: 0, approved: 0, declined: 0, invoiced: 0, paid: 0, overdue: 0 };
  for (const truck of trucks) {
    const m = moneyState(truck, now);
    if (m.internal || totals[m.stage] === undefined) continue;
    totals[m.stage] += m.stage === 'invoiced' ? m.remaining : m.total;
    if (m.overdueMs > 0) totals.overdue += m.remaining;
  }
  return totals;
}
